import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

import Button from '@jobcase/react_pattern_library/components/Button';
import Checkbox from '@jobcase/react_pattern_library/components/Checkbox';
import InfoLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/InfoLightSVG';
import Input from '@jobcase/react_pattern_library/components/Input/InputRebrand';
import LinkTypography from '@jobcase/react_pattern_library/components/Link/LinkRebrand';
import SVGIcon from '@jobcase/react_pattern_library/components/SVGIcon';
import Typography from '@jobcase/react_pattern_library/components/Typography/TypographyRebrand';
import BREAKPOINT from '@jobcase/react_pattern_library/styles/breakpoint/breakpointRebrand';
import COLOR from '@jobcase/react_pattern_library/styles/color/colorRebrand';
import FONT from '@jobcase/react_pattern_library/styles/font/fontRebrand';
import SPACING from '@jobcase/react_pattern_library/styles/spacing/spacingRebrand';
import Spinner from '@jobcase/react_pattern_library/components/Spinner';
import styles from '@jobcase/react_pattern_library/styles/rebrand';

import actions, { attemptSignup } from '../../redux/nodes/authentication/actions';
import { getArrivalEmail, getArrivalFirstName, getArrivalLastName, getIsSignupLoader } from '../../redux/nodes/authentication/selectors';

import {
  reportJobcaseCreateAccNativeAuthClick,
  reportJobcaseCreateAccNativeAuthError,
  reportJobcaseSsoViewRendered,
  AUTH_ERROR_REASON,
  SSO_PAGE,
} from '../../utils/reporting/ssoEvents';
import { useSelector } from '../../utils/hooks';
import { getArrivalKey } from '../../utils/cookie';
import {
  isJobcaseEmail,
  isValidEmail,
  isValidPassword,
  isValidPasswordOld,
  containsInvalidCharacters,
  getInvalidCharacters,
  getInvalidCharactersErrorMessage,
} from '../../utils/validation';
import { SIGNUP_VARIANTS } from '../../utils/constants/SSOConstants';
import { PAGES } from '../../redux/nodes/router/actions';

const JOBCASE = 'https://www.jobcase.com';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  [class*="InputRebrand__FormFieldWrapper"] {
    @media (min-width: ${BREAKPOINT.md}) {
      margin-bottom: ${SPACING.small}px;
    }
  }
`;

const PrivacyTypography = styled(Typography)`
  width: 100%;

  a {
    border: 0;
    font-size: ${FONT.fontSize.tertiary};
    font-weight: 400;
    line-height: ${FONT.lineHeight.tertiary};
    padding: 0;
    text-decoration: underline ;
  }
`;

const CheckboxWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: flex-start;
`;

const StyledCheckbox = styled(Checkbox)`
  input {
    margin-top: 1px;

    ${({ hasError }) => hasError && css`
      background-color: ${styles.color.lightErrorColor};
    `}
  }
`;

const ErrorWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: flex-start;
  margin-top: ${SPACING.extraTiny}px;

  svg {
    height: 23px;
    margin-right: ${SPACING.extraTiny}px;
    width: 22px;
  }

  p {
    color: ${COLOR.errorTextColor};
    flex: 1;
    font-family: ${FONT.fontFamily};
    font-size: ${FONT.fontSize.body};
    font-weight: 600 ;
    line-height: ${FONT.lineHeight.h3};
    margin: 0;
  }
`;

const StyledInput = styled(Input)`
  input {
    border: 0;

    :-webkit-autofill {
      ${({ hasError }) => hasError && css`
        border: 2px solid ${styles.color.errorColor};
        box-shadow: 0 0 0 30px ${styles.color.lightErrorColor} inset;
      `}
    }
  }
`;

const CustomButtonRebrand = styled(Button)`
  margin: 0 0 ${SPACING.small}px;
  padding: 0;
  width: 100%;

  ${({ isLoading }) => isLoading && css`
    pointer-events: none;
  `}
`;

const StyledLink = styled(LinkTypography)`
  cursor: pointer;
`;

const ServicesAgreement = () => (
  <StyledLink target="_blank" href={`${JOBCASE}${PAGES.SERVICES_AGREEMENT}`}>
    Jobcase Services Agreement
  </StyledLink>
);

const TermsLink = () => (
  <StyledLink target="_blank" href={`${JOBCASE}${PAGES.TERMS_OF_USE}`}>
    Terms of Use
  </StyledLink>
);

const PrivacyLink = () => (
  <StyledLink target="_blank" href={`${JOBCASE}${PAGES.PRIVACY_POLICY}`}>
    Privacy Policy
  </StyledLink>
);

const JobcasePublisherAgreement = () => (
  <StyledLink target="_blank" href={`${JOBCASE}${PAGES.PUBLISHER_AGREEMENT}`}>
    Jobcase Publisher Agreement
  </StyledLink>
);

const NativeSignup = ({ variant, onSignupSuccess, onFormChange }) => {
  const dispatch = useDispatch();
  const arrivalEmail = useSelector(getArrivalEmail);
  const arrivalFirstName = useSelector(getArrivalFirstName);
  const arrivalLastName = useSelector(getArrivalLastName);
  const error = useSelector(s => s.authentication.loginError);
  const isSignupLoader = useSelector(getIsSignupLoader);

  const claimingKnownProfile = [SIGNUP_VARIANTS.CONFIRM_EMAIL, SIGNUP_VARIANTS.CONFIRM_EMAIL_V2, SIGNUP_VARIANTS.CLAIM_PROFILE].includes(variant);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [iAgree, setIAgree] = useState(false);

  useEffect(() => {
    reportJobcaseSsoViewRendered(SSO_PAGE.SIGNUP);
  }, []);

  useEffect(() => {
    if (claimingKnownProfile) {
      if (!arrivalEmail) {
        dispatch(actions.getProfileArrivalInfo());
      } else {
        setEmail(arrivalEmail);
        setFirstName(arrivalFirstName);
        setLastName(arrivalLastName);
      }
    }
  }, [variant, arrivalEmail, arrivalFirstName, arrivalLastName]);

  const submit = async () => {
    // clear any previous errors
    dispatch({
      type: actions.TYPES.SET_LOGIN_ERROR,
      payload: null,
    });

    let isValid = true;
    const errObj = {};

    if (firstName.length < 1) {
      errObj.firstName = 'First name required';
      reportJobcaseCreateAccNativeAuthError(AUTH_ERROR_REASON.FIRST_NAME);
      isValid = false;
    } else if (containsInvalidCharacters(firstName)) {
      const invalidChars = getInvalidCharacters(firstName);
      errObj.firstName = getInvalidCharactersErrorMessage(invalidChars);
      reportJobcaseCreateAccNativeAuthError(AUTH_ERROR_REASON.FIRST_NAME);
      isValid = false;
    }

    if (lastName.length < 1) {
      errObj.lastName = 'Last name required';
      if (isValid) reportJobcaseCreateAccNativeAuthError(AUTH_ERROR_REASON.LAST_NAME);
      isValid = false;
    } else if (containsInvalidCharacters(lastName)) {
      const invalidChars = getInvalidCharacters(lastName);
      errObj.lastName = getInvalidCharactersErrorMessage(invalidChars);
      if (isValid) reportJobcaseCreateAccNativeAuthError(AUTH_ERROR_REASON.LAST_NAME);
      isValid = false;
    }

    if (!isValidEmail(email)) {
      errObj.email = 'Valid email address required';
      if (isValid) reportJobcaseCreateAccNativeAuthError(AUTH_ERROR_REASON.EMAIL);
      isValid = false;
    }

    if (isJobcaseEmail(email)) {
      errObj.email = 'Please enter a non-jobcase email address';
      if (isValid) reportJobcaseCreateAccNativeAuthError(AUTH_ERROR_REASON.EMAIL);
      isValid = false;
    }

    if (!password) {
      errObj.password = 'Password required';
      if (isValid) reportJobcaseCreateAccNativeAuthError(AUTH_ERROR_REASON.PASSWORD, false);
    }

    if (!isValidPassword(password)) {
      if (isValid) reportJobcaseCreateAccNativeAuthError(AUTH_ERROR_REASON.PASSWORD, false);
    }

    if (!isValidPasswordOld(password)) {
      if (!errObj.password) errObj.password = 'Enter a password with at least 8 characters';
      if (isValid) reportJobcaseCreateAccNativeAuthError(AUTH_ERROR_REASON.PASSWORD, true);
      isValid = false;
    }

    if (!iAgree) {
      errObj.iAgree = 'You must agree to the Jobcase Services Agreement and Terms of Use to continue';
      isValid = false;
    }

    if (!isValid) {
      dispatch({
        type: actions.TYPES.SET_LOGIN_ERROR,
        payload: { ...errObj },
      });
    } else {
      const arrivalKey = getArrivalKey();
      const signedUp = await dispatch(attemptSignup({
        first_name: firstName,
        last_name: lastName,
        email,
        password,
      }, arrivalKey));

      if (signedUp && onSignupSuccess) onSignupSuccess('Jobcase');
      reportJobcaseCreateAccNativeAuthClick();
    }
  };

  const checkForEnterKey = (e) => {
    if (e.key !== 'Enter') return;
    submit();
  };

  useEffect(() => {
    if (firstName || lastName || email || password || iAgree) {
      onFormChange(true);
    } else {
      onFormChange(false);
    }
  }, [firstName, lastName, email, password, iAgree]);

  const resetErrorForField = (field) => {
    const errObj = { ...error, [field]: null };

    if (field === 'email' || field === 'password') {
      errObj.signup = null;
    }

    dispatch({
      type: actions.TYPES.SET_LOGIN_ERROR,
      payload: { ...errObj },
    });
  };

  const handleFirstNameInput = e => setFirstName(e.target.value);
  const handleLastNameInput = e => setLastName(e.target.value);
  const handleEmailInput = (e) => {
    resetErrorForField('email');
    setEmail(e.target.value);
  };
  const handlePasswordInput = (e) => {
    resetErrorForField('password');
    setPassword(e.target.value);
  };

  const onChangeCheckbox = () => {
    resetErrorForField('iAgree');
    setIAgree(val => !val);
  };

  return (
    <Wrapper>
      <StyledInput
        placeholder="First name"
        value={firstName}
        onChange={handleFirstNameInput}
        onKeyPress={checkForEnterKey}
        hasError={error?.firstName}
        errorMessage={error?.firstName}
        onFocus={() => resetErrorForField('firstName')}
        data-fullstory-unmask
      />
      <StyledInput
        placeholder="Last name"
        value={lastName}
        onChange={handleLastNameInput}
        onKeyPress={checkForEnterKey}
        hasError={error?.lastName}
        errorMessage={error?.lastName}
        onFocus={() => resetErrorForField('lastName')}
        data-fullstory-unmask
      />
      <StyledInput
        placeholder="Email address"
        value={email}
        onChange={handleEmailInput}
        onKeyPress={checkForEnterKey}
        isDisabled={claimingKnownProfile}
        hasError={error?.signup || error?.email}
        errorMessage={error?.signup ? null : error?.email}
        onFocus={() => resetErrorForField('email')}
      />
      <StyledInput
        placeholder="Password (min 8 characters)"
        value={password}
        type="password"
        onChange={handlePasswordInput}
        onKeyPress={checkForEnterKey}
        hasError={error?.signup || error?.password}
        errorMessage={error?.signup || error?.password}
        onFocus={() => resetErrorForField('password')}
      />
      <CustomButtonRebrand
        color={Button.COLORS.SECONDARY}
        onClick={submit}
        isLoading={isSignupLoader}
      >
        {isSignupLoader ? <Spinner color={styles.color.jobcaseWhiteColor} /> : 'Join'}
      </CustomButtonRebrand>
      <CheckboxWrapper>
        <StyledCheckbox isChecked={iAgree} onChange={onChangeCheckbox} hasError={error?.iAgree} />
        {/* eslint-disable-next-line */}
        <PrivacyTypography variant={Typography.VARIANTS.TERTIARY}>
          By checking this box, you agree to the
          {' '}
          <ServicesAgreement />
          , the
          {' '}
          <JobcasePublisherAgreement />
          , and the
          {' '}
          <TermsLink />
          . You also agree to receive emails from Jobcase regarding product and service updates and marketing emails. You may
          unsubscribe from marketing emails at any time. Learn more about how we use and protect your personal data in our
          {' '}
          <PrivacyLink />
          .
        </PrivacyTypography>

      </CheckboxWrapper>
      {error?.iAgree && (
        <ErrorWrapper>
          <SVGIcon svgChild={InfoLightSVG} color={styles.color.errorColor} />
          <p>{error?.iAgree}</p>
        </ErrorWrapper>
      )}
    </Wrapper>
  );
};

NativeSignup.propTypes = {
  /**
   * URL destination after successful authentication
   */
  variant: PropTypes.oneOf(Object.values(SIGNUP_VARIANTS)),
  /**
   * Callback fired on successful signup to perform side effects
   */
  onSignupSuccess: PropTypes.func,
  /**
   * Function to set whether the form is changed or not
   */
  onFormChange: PropTypes.func,
};

NativeSignup.defaultProps = {
  variant: SIGNUP_VARIANTS.DEFAULT,
  onSignupSuccess: null,
  onFormChange: PropTypes.func,
};

export default NativeSignup;
